/**
 * Experience orbs (vanilla ExperienceOrb), DOM-free.
 *
 *   const xp = new XpOrbManager();
 *   xp.spawn(x, y, z, amount)     // ExperienceOrb.award: split into vanilla orb sizes (merging into nearby same-value orbs)
 *   xp.tick(host)                 // once per 20 Hz game tick
 *   xp.views()                    // reused XpOrbView[] for ItemEntityRenderer.update
 *
 * Vanilla numbers: 0.5 hitbox, gravity 0.03, floats in water, hops out of lava, drifts toward the
 * nearest player within 8 blocks (pull (1 - d/8)^2 * 0.1 along the eye-height-midpoint vector),
 * 2 tick delay between pickups, 6000 tick life, merging with same-value orbs every 20 ticks.
 */

import { collisionOf, IS_LAVA, IS_WATERY, NO_BOXES } from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import { boxHits, clipMove, type ShapesAt } from '../world/collide';

export interface XpHost {
  getBlock(x: number, y: number, z: number): number;
  player: { x: number; y: number; z: number; eyeHeight?: number; height?: number; width?: number; alive: boolean; spectator?: boolean };
  /** PlayerStats.addXp (the lead passes `stats`). */
  stats: { addXp(points: number): void };
  light?(x: number, y: number, z: number): number;
  /** False while the chunk at (x, z) is not loaded: the orb freezes there. */
  loaded?(x: number, z: number): boolean;
  /** An orb was absorbed (levelup/pickup ding). */
  onPickup?(points: number): void;
  /**
   * Dedicated server: every player who may absorb orbs. When present, `player`
   * / `stats` are ignored: an orb drifts to the nearest picker and each picker
   * has its own 2 tick take delay.
   */
  pickers?: readonly XpPicker[];
}

/** One player on the server, as XP orbs see it. */
export interface XpPicker {
  x: number; y: number; z: number;
  eyeHeight?: number; height?: number; width?: number;
  alive: boolean; spectator?: boolean;
  addXp(points: number): void;
}

export interface XpOrbView {
  readonly id: number;
  /** Value of one orb (picks the sprite); `count` orbs were merged into it. */
  value: number;
  count: number;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  age: number;
  light: number;
}

const XP_LIFETIME = 6000;
const HALF = 0.25;
const SIZE = 0.5;
const EPS = 1e-4;

/** ExperienceOrb.getExperienceValue: the orb size a pile of points is cut into. */
export function orbValueFor(amount: number): number {
  for (const v of [2477, 1237, 617, 307, 149, 73, 37, 17, 7, 3]) if (amount >= v) return v;
  return 1;
}

/** ExperienceOrb.getIcon: cell (0..10) of the 4x4 sheet for an orb value. */
export function orbIconFor(value: number): number {
  const t = [2477, 1237, 617, 307, 149, 73, 37, 17, 7, 3];
  for (let i = 0; i < t.length; i++) if (value >= t[i]) return 10 - i;
  return 0;
}

class XpOrb {
  x = 0; y = 0; z = 0;
  vx = 0; vy = 0; vz = 0;
  px = 0; py = 0; pz = 0;
  age = 0;
  count = 1;
  onGround = false;
  dead = false;
  readonly view: XpOrbView;
  constructor(readonly id: number, public value: number) {
    this.view = { id, value, count: 1, x: 0, y: 0, z: 0, px: 0, py: 0, pz: 0, age: 0, light: 1 };
  }
}

export class XpOrbManager {
  private readonly orbs: XpOrb[] = [];
  private nextId = 1;
  private tickCount = 0;
  private takeDelay = 0;
  private readonly rnd: () => number;
  private readonly box = [0, 0, 0, 0, 0, 0];
  private readonly viewList: XpOrbView[] = [];
  private host!: XpHost;
  private readonly shapesAt: ShapesAt = (x, y, z) =>
    y < 0 || y >= WORLD_HEIGHT ? NO_BOXES : collisionOf(this.host.getBlock(x, y, z));

  constructor(opts: { rnd?: () => number; maxOrbs?: number; allocId?: () => number } = {}) {
    this.rnd = opts.rnd ?? Math.random;
    this.maxOrbs = opts.maxOrbs ?? 256;
    if (opts.allocId) this.allocId = opts.allocId;
  }
  private readonly maxOrbs: number;
  /** Entity ids: own counter, or the server's shared one. */
  private allocId: () => number = () => this.nextId++;
  /** Server pickers: tick at which each may take its next orb. */
  private readonly nextTake = new WeakMap<XpPicker, number>();

  /** Nearest living picker to an orb (the one it drifts to and that may absorb it). */
  private nearestPicker(o: XpOrb, pickers: readonly XpPicker[]): XpPicker | null {
    let best: XpPicker | null = null, bd = Infinity;
    for (const p of pickers) {
      if (!p.alive || p.spectator) continue;
      const d = (p.x - o.x) ** 2 + (p.y - o.y) ** 2 + (p.z - o.z) ** 2;
      if (d < bd) { bd = d; best = p; }
    }
    return best;
  }

  get count(): number { return this.orbs.length; }

  /** Save payload (entities/persist.ts): [value, count, x, y, z, vx, vy, vz, age] per orb. */
  serialize(): number[][] {
    return this.orbs.filter((o) => !o.dead).map((o) => [o.value, o.count, o.x, o.y, o.z, o.vx, o.vy, o.vz, o.age]);
  }

  /** Replace every orb with a saved payload (see serialize). */
  load(rows: readonly (readonly number[])[]): void {
    this.orbs.length = 0;
    this.viewList.length = 0;
    for (const r of rows) {
      if (!Array.isArray(r) || r.length < 9 || !(r[0] > 0)) continue;
      const o = new XpOrb(this.allocId(), r[0]);
      o.count = Math.max(1, r[1] | 0);
      o.x = o.px = r[2]; o.y = o.py = r[3]; o.z = o.pz = r[4];
      o.vx = r[5]; o.vy = r[6]; o.vz = r[7]; o.age = r[8] | 0;
      this.orbs.push(o);
    }
  }
  /** Total points lying in the world. */
  get total(): number { let n = 0; for (const o of this.orbs) n += o.value * o.count; return n; }

  /** Award `amount` points as vanilla-sized orbs at (x, y, z) (the centre of the dead mob / broken ore). */
  spawn(x: number, y: number, z: number, amount: number): void {
    amount = Math.floor(amount);
    while (amount > 0) {
      const v = orbValueFor(amount);
      amount -= v;
      if (this.mergeInto(x, y, z, v)) continue;
      const o = new XpOrb(this.allocId(), v);
      o.x = o.px = x; o.y = o.py = y - SIZE / 2; o.z = o.pz = z;
      o.vx = (this.rnd() * 0.2 - 0.1) * 2;
      o.vy = this.rnd() * 0.2 * 2;
      o.vz = (this.rnd() * 0.2 - 0.1) * 2;
      this.orbs.push(o);
      if (this.orbs.length > this.maxOrbs) this.orbs.shift();
    }
  }

  /** ExperienceOrb.tryMergeToExisting: a young same-value orb right there swallows the new one. */
  private mergeInto(x: number, y: number, z: number, value: number): boolean {
    for (const o of this.orbs) {
      if (o.value === value && o.age < 100 && Math.abs(o.x - x) < 0.5 && Math.abs(o.y + SIZE / 2 - y) < 0.5 && Math.abs(o.z - z) < 0.5) {
        o.count++;
        o.age = Math.min(o.age, 0);
        return true;
      }
    }
    return false;
  }

  tick(host: XpHost): void {
    this.host = host;
    this.tickCount++;
    if (this.takeDelay > 0) this.takeDelay--;
    const pickers = host.pickers;
    let p = host.player;
    let eye = p.eyeHeight ?? (p.height ?? 1.8) * 0.9;
    let reach = (p.width ?? 0.6) / 2 + HALF;
    for (let i = this.orbs.length - 1; i >= 0; i--) {
      const o = this.orbs[i];
      o.px = o.x; o.py = o.y; o.pz = o.z;
      if (host.loaded && !host.loaded(o.x, o.z)) continue;
      if (pickers) {
        const np = this.nearestPicker(o, pickers);
        if (np) { p = np; eye = np.eyeHeight ?? (np.height ?? 1.8) * 0.9; reach = (np.width ?? 0.6) / 2 + HALF; }
        this.step(o, np ?? host.player, eye);
        if (o.dead || o.age >= XP_LIFETIME || o.y < -64) { this.orbs.splice(i, 1); continue; }
        if (np && (this.nextTake.get(np) ?? 0) <= this.tickCount
          && Math.abs(o.x - np.x) < reach && Math.abs(o.z - np.z) < reach
          && o.y < np.y + (np.height ?? 1.8) && o.y + SIZE > np.y) {
          this.nextTake.set(np, this.tickCount + 2);
          np.addXp(o.value);
          host.onPickup?.(o.value);
          if (--o.count <= 0) this.orbs.splice(i, 1);
        }
        continue;
      }
      this.step(o, p, eye);
      if (o.dead || o.age >= XP_LIFETIME || o.y < -64) { this.orbs.splice(i, 1); continue; }
      if (this.takeDelay === 0 && p.alive && !p.spectator
        && Math.abs(o.x - p.x) < reach && Math.abs(o.z - p.z) < reach
        && o.y < p.y + (p.height ?? 1.8) && o.y + SIZE > p.y) {
        this.takeDelay = 2;
        host.stats.addXp(o.value);
        host.onPickup?.(o.value);
        if (--o.count <= 0) this.orbs.splice(i, 1);
      }
    }
    const out = this.viewList;
    out.length = 0;
    for (const o of this.orbs) {
      if (o.dead) continue;
      const v = o.view;
      v.value = o.value; v.count = o.count;
      v.x = o.x; v.y = o.y; v.z = o.z; v.px = o.px; v.py = o.py; v.pz = o.pz;
      v.age = o.age;
      v.light = host.light ? host.light(o.x, o.y + 0.25, o.z) : 1;
      out.push(v);
    }
  }

  views(): readonly XpOrbView[] { return this.viewList; }
  clear(): void { this.orbs.length = 0; this.viewList.length = 0; }

  private step(o: XpOrb, p: XpHost['player'], eye: number): void {
    const h = this.host;
    const cx = Math.floor(o.x), cy = Math.floor(o.y + 0.25), cz = Math.floor(o.z);
    const here = cy < 0 || cy >= WORLD_HEIGHT ? 0 : h.getBlock(cx, cy, cz);
    if (IS_LAVA[here] === 1) {
      o.vx = (this.rnd() - this.rnd()) * 0.2; o.vy = 0.2; o.vz = (this.rnd() - this.rnd()) * 0.2;
    } else if (IS_WATERY[here]) {
      o.vy = Math.min(o.vy + 0.002, 0.06); // vanilla: drifts up slowly while the eyes are in water
      o.vx *= 0.99; o.vz *= 0.99;
    } else {
      o.vy -= 0.03;
    }

    const box = this.box;
    box[0] = o.x - HALF; box[1] = o.y; box[2] = o.z - HALF;
    box[3] = o.x + HALF; box[4] = o.y + SIZE; box[5] = o.z + HALF;
    if (boxHits(box, EPS, this.shapesAt)) {
      o.y += 0.1; o.vx = o.vy = o.vz = 0;
      box[1] += 0.1; box[4] += 0.1;
    }

    // magnetise toward the nearest player within 8 blocks
    if (p.alive && !p.spectator) {
      const dx = p.x - o.x, dy = p.y + eye / 2 - (o.y + SIZE / 2), dz = p.z - o.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < 64) {
        const d = Math.sqrt(d2);
        const s = 1 - d / 8;
        const k = (s * s * 0.1) / Math.max(d, 1e-6);
        o.vx += dx * k; o.vy += dy * k; o.vz += dz * k;
      }
    }

    // move: y first, then the longer horizontal axis (Entity.move)
    const shapes = this.shapesAt;
    const cyv = clipMove(box, 1, o.vy, EPS, shapes);
    box[1] += cyv; box[4] += cyv;
    o.onGround = o.vy < 0 && cyv > o.vy;
    if (cyv !== o.vy) o.vy = 0;
    for (const axis of Math.abs(o.vx) >= Math.abs(o.vz) ? [0, 2] : [2, 0]) {
      const want = axis === 0 ? o.vx : o.vz;
      const c = clipMove(box, axis, want, EPS, shapes);
      box[axis] += c; box[axis + 3] += c;
      if (c !== want) { if (axis === 0) o.vx = 0; else o.vz = 0; }
    }
    o.x = (box[0] + box[3]) / 2; o.y = box[1]; o.z = (box[2] + box[5]) / 2;

    let f = 0.98;
    if (o.onGround) f = 0.6 * 0.98;
    o.vx *= f; o.vy *= 0.98; o.vz *= f;
    if (o.onGround) o.vy *= -0.9;

    if (this.tickCount % 20 === 1) this.scanMerge(o);
    o.age++;
  }

  /** Fold same-value neighbours into this orb (ExperienceOrb.scanForMerges). */
  private scanMerge(o: XpOrb): void {
    for (const other of this.orbs) {
      if (other === o || other.dead || other.value !== o.value) continue;
      if (Math.abs(other.x - o.x) > 0.5 + SIZE || Math.abs(other.z - o.z) > 0.5 + SIZE || Math.abs(other.y - o.y) > SIZE) continue;
      o.count += other.count;
      o.age = Math.min(o.age, other.age);
      other.dead = true;
      other.count = 0;
    }
  }
}
